import { Sparkles, BarChart3, Bot, CalendarCheck, Trophy } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useReveal } from "@/lib/useReveal";
import { CountUp } from "./CountUp";

export const Features = () => {
  const ref = useReveal();
  return (
    <section id="features" data-testid="features-section" className="relative py-20 sm:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className="reveal text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs uppercase tracking-widest text-white/70">
            <Sparkles className="h-3.5 w-3.5 text-neon-lime" />
            Why Pulse
          </div>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight text-white">
            A TRAINING ENGINE <span className="text-neon-lime">BUILT FOR RESULTS</span>
          </h2>
          <p className="mt-3 text-white/70">
            Four systems working together so every session moves the needle.
          </p>
        </div>

        <div className="grid gap-4 sm:gap-6 lg:grid-cols-12 lg:grid-rows-2">
          {/* Stat card */}
          <Card
            data-testid="feature-card-stat"
            className="card-lift lg:col-span-5 lg:row-span-2 bg-white/[0.03] border-white/10 rounded-2xl overflow-hidden relative"
          >
            <div
              className="absolute inset-0 opacity-70 pointer-events-none"
              style={{
                background:
                  "radial-gradient(500px circle at 20% 0%, rgba(191,255,0,0.18), transparent 60%), radial-gradient(400px circle at 90% 100%, rgba(15,240,252,0.12), transparent 60%)",
              }}
            />
            <CardContent className="relative p-8 flex flex-col h-full">
              <div className="text-xs uppercase tracking-widest text-white/60">Measured Impact</div>
              <div className="mt-2 font-display text-7xl sm:text-8xl leading-none text-white">
                +<CountUp end={23} />%
              </div>
              <div className="mt-2 text-white/80 text-lg">
                <span className="text-neon-lime font-semibold">Consistency lift</span> in just 4 weeks
              </div>
              <p className="mt-3 text-white/60 text-sm">
                Members who follow their Pulse plan complete 23% more sessions compared to their first two weeks.
              </p>
              <div className="mt-auto pt-8">
                <div className="flex items-end justify-between gap-3">
                  {[32, 48, 40, 62, 54, 72, 80].map((h, i) => (
                    <div
                      key={i}
                      className="flex-1 rounded-md"
                      style={{
                        height: `${h}px`,
                        background:
                          i === 6
                            ? "linear-gradient(180deg, rgba(191,255,0,0.9), rgba(191,255,0,0.2))"
                            : i === 5
                            ? "linear-gradient(180deg, rgba(15,240,252,0.85), rgba(15,240,252,0.15))"
                            : "rgba(255,255,255,0.10)",
                      }}
                    />
                  ))}
                </div>
                <div className="mt-2 flex items-center justify-between text-[10px] uppercase tracking-widest text-white/45">
                  <span>Wk 1</span>
                  <span>Wk 7</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <FeatureTile
            icon={Bot}
            iconClass="text-neon-cyan bg-neon-cyan/10 border-neon-cyan/25"
            title="Auto-adjusting intensity"
            copy="Your plan recalibrates after every workout based on RPE, rest, and recent PRs."
            className="lg:col-span-4"
            testid="feature-card-auto"
          />
          <FeatureTile
            icon={CalendarCheck}
            iconClass="text-neon-orange bg-neon-orange/10 border-neon-orange/25"
            title="Coach feedback in 24h"
            copy="Upload a set. Get a certified coach&rsquo;s review — form cues, tempo, weight targets."
            className="lg:col-span-3"
            testid="feature-card-coach"
          />
          <FeatureTile
            icon={BarChart3}
            iconClass="text-neon-lime bg-neon-lime/10 border-neon-lime/25"
            title="Proof you&rsquo;re improving"
            copy="Volume, tonnage, HR zones and PRs — tracked automatically in one clean dashboard."
            className="lg:col-span-4"
            testid="feature-card-proof"
          />
          <FeatureTile
            icon={Trophy}
            iconClass="text-neon-lime bg-neon-lime/10 border-neon-lime/25"
            title="Streaks that stick"
            copy="Micro-goals, flexible rest days, and nudges when it&rsquo;s time to push."
            className="lg:col-span-3"
            testid="feature-card-streaks"
          />
        </div>
      </div>
    </section>
  );
};

const FeatureTile = ({ icon: Icon, iconClass, title, copy, className = "", testid }) => {
  const ref = useReveal();
  return (
    <Card
      ref={ref}
      data-testid={testid}
      className={`card-lift reveal bg-white/[0.03] border-white/10 rounded-2xl hover:border-neon-lime/30 hover:shadow-card ${className}`}
    >
      <CardContent className="p-6">
        <div className={`inline-flex h-10 w-10 items-center justify-center rounded-xl border ${iconClass}`}>
          <Icon className="h-5 w-5" />
        </div>
        <h3 className="mt-4 text-white font-semibold text-lg leading-tight">{title}</h3>
        <p className="mt-2 text-sm text-white/65 leading-relaxed" dangerouslySetInnerHTML={{ __html: copy }} />
      </CardContent>
    </Card>
  );
};
